import { useCallback, useEffect, useState } from 'react';

import { useElements, useStripe } from '@stripe/react-stripe-js';
import { type PaymentMethod, type StripeError } from '@stripe/stripe-js';
import {
  type EmbeddedPaymentElementResult,
  type IntentCreationCallbackParams,
} from '@stripe/stripe-react-native';

import { type IconName } from '@sg/garnish';

import { StripeLogger } from '../../utils';
import { getSelectedPaymentMethod } from './helpers';
import { useLabelAndIconByType, usePaymentMethodChangeHandler } from './hooks';

import type { UseStripePaymentProps } from '../../types';

export const useStripePaymentElement = (props: UseStripePaymentProps) => {
  const { paymentMethods, stripeIntentConfigRef } = props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState<PaymentMethod | null>(null); // prettier-ignore
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState(''); // prettier-ignore
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] = useState<IconName>('IconCreditCard'); // prettier-ignore
  const { isApplePay, isGooglePay, isLink, isCashApp } = getSelectedPaymentMethod(selectedPaymentMethodType); // prettier-ignore

  // ─── Payment Method Creation ───────────────────────────────────────────────

  const createPaymentMethod = useCallback(
    async (shouldSubmit = false) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized');
      }

      if (shouldSubmit) {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          StripeLogger.logSubmitError(submitError);
          throw new Error(submitError.message);
        }
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        StripeLogger.logCreatePaymentMethodError(error);
        throw new Error(error.message);
      }

      setCreatedPaymentMethod(paymentMethod);

      return paymentMethod;
    },
    [elements, stripe],
  );

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();
  const handlePaymentElementChange = usePaymentMethodChangeHandler({
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodType,
    setSelectedPaymentIcon,
    createPaymentMethod,
  });

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) {
      return;
    }

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async (): Promise<ConfirmResult> => {
    try {
      if (!stripe || !elements) {
        StripeLogger.logStripeNotInitializedError();
        return toFailure('Stripe not initialized');
      }

      const { error: submitError, selectedPaymentMethod } = await elements.submit();

      if (submitError) {
        StripeLogger.logSubmitError(submitError);
        return toFailure(submitError.message);
      }

      const paymentMethod = createdPaymentMethod || (await createPaymentMethod()); // prettier-ignore
      const paymentMethodId = paymentMethod?.id;
      const confirmHandler = stripeIntentConfigRef?.current?.confirmHandler;

      if (!confirmHandler) {
        return toFailure('Stripe confirm handler is not available');
      }

      // Maps the stripe-react-native interface to web.
      return await new Promise<ConfirmResult>((resolve) => {
        const confirmIntent = async (
          params: IntentCreationCallbackParams,
        ): Promise<ConfirmResult> => {
          if (!params.clientSecret) {
            return toFailure(params.error?.message ?? 'No client secret');
          }

          const result = await stripe.confirmPayment({
            elements,
            clientSecret: params.clientSecret,
            redirect: redirectCondition,
            confirmParams: { return_url: defaultRedirect },
          });

          if (result?.error) {
            return toFailure(result.error.message, result.error.code);
          }

          if (result?.paymentIntent?.status === 'requires_action') {
            return { status: 'canceled' };
          }

          return { status: 'completed' };
        };

        const resolveWithError = (error: unknown) => {
          StripeLogger.logGenericError(toError(error));
          resolve(toFailure(error));
        };

        // `confirmHandler` is async, so its rejection has to be routed back to this promise.
        void Promise.resolve(
          confirmHandler(
            {
              id: paymentMethodId,
              paymentMethodType: selectedPaymentMethod,
            } as never,
            checkIfShouldSaveCard(paymentMethod, paymentMethods),
            (params) => {
              void confirmIntent(params).then(resolve, resolveWithError);
            },
          ),
        ).catch(resolveWithError);
      });
    } catch (error) {
      StripeLogger.logGenericError(toError(error));
      return toFailure(error);
    }
  }, [
    stripe,
    elements,
    createdPaymentMethod,
    createPaymentMethod,
    stripeIntentConfigRef,
    paymentMethods,
  ]);

  return {
    isUpdatingTotal: false,
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    selectedPaymentMethodType,
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirmStripe,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * We should only save the card if the payment method is a card that is not already stored on the customer.
 */
function checkIfShouldSaveCard(
  paymentMethod: PaymentMethod,
  paymentMethods: UseStripePaymentProps['paymentMethods'],
) {
  const isCard = paymentMethod?.type === 'card';
  const isWalletPay = Boolean(paymentMethod?.card?.wallet);
  const isAlreadyStored = Boolean(paymentMethod?.customer) || paymentMethods.some((pm) => pm.id === paymentMethod?.id); // prettier-ignore

  return isCard && !isWalletPay && !isAlreadyStored;
}

/**
 * `stripe-react-native` reports failures as an `Error`, but Stripe.js messages are optional
 * and thrown values are unknown, so everything is normalized to an `Error` at this boundary.
 */
function toError(value: unknown) {
  if (value instanceof Error) {
    return value;
  }

  return new Error(typeof value === 'string' && value ? value : GENERIC_ERROR_MESSAGE);
}

function toFailure(error: unknown, code?: StripeError['code']): ConfirmResult {
  return { status: 'failed', error: toError(error), code };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
const GENERIC_ERROR_MESSAGE = 'Something went wrong confirming the payment';

// ─── Types ───────────────────────────────────────────────────────────────────

type ConfirmResult = EmbeddedPaymentElementResult & { code?: StripeError['code'] };
